/**
 * What the lock screen says while a long-form recording plays.
 *
 * The native half is Info.plist's `audio` background mode plus the
 * AVAudioSession in AppDelegate: together they stop iOS suspending
 * playback when the phone locks. That gets you sound and nothing else -
 * a lock screen with a blank transport, which reads as broken rather
 * than as a podcast.
 *
 * This is the other half. WKWebView maps the standard MediaSession API
 * onto iOS's Now Playing centre, so setting metadata and action handlers
 * here is what puts the episode title, the artwork and working
 * play/pause/skip buttons on the lock screen. No custom Capacitor plugin
 * and no native bridge: the browser API is the bridge.
 *
 * SCOPED ON PURPOSE. Only the Weekly Q&A reader and masterclasses call
 * this. A forty-five minute Q&A is one person talking and belongs in
 * your ears while you drive; a thirty-second drill clip is all picture,
 * and a phone that keeps playing drill audio from a pocket reads as a
 * bug. The capability is declared app-wide, so the discipline about what
 * uses it has to live here.
 *
 * Everything is feature-detected and wrapped. MediaSession is absent on
 * older iOS and partial on some Androids, and a missing lock screen
 * title must never take down the page that was playing fine.
 */

const SKIP_SECONDS = 15;

function hasMediaSession() {
  return typeof navigator !== "undefined" && "mediaSession" in navigator;
}

/**
 * setNowPlaying — describe what is playing, and wire the buttons.
 *
 * @param {object}   meta            { title, artist, album, artwork }
 * @param {function} getMedia        returns the <video>/<audio> element, or null
 * @returns {function} teardown
 *
 * `getMedia` is a getter rather than the element itself because the Mux
 * player mounts its media asynchronously inside a shadow root: at the
 * moment this is called there is often nothing to hold yet, and holding
 * a stale element would leave the lock screen buttons wired to a video
 * that has since been replaced.
 */
export function setNowPlaying(meta, getMedia) {
  if (!hasMediaSession()) return () => {};
  const ms = navigator.mediaSession;

  try {
    // eslint-disable-next-line no-undef
    ms.metadata = new window.MediaMetadata({
      title: meta?.title || "BAM Coaches",
      artist: meta?.artist || "By Any Means Coaches",
      album: meta?.album || "",
      artwork: meta?.artwork
        ? [{ src: meta.artwork, sizes: "960x540", type: "image/jpeg" }]
        : [],
    });
  } catch { /* metadata is a nicety; the audio is the product */ }

  const media = () => { try { return getMedia?.() || null; } catch { return null; } };

  // Fifteen seconds, not thirty. The thing a listener rewinds for is a
  // sentence they missed, and thirty seconds overshoots it into the
  // previous point.
  const handlers = {
    play:          () => { media()?.play?.(); },
    pause:         () => { media()?.pause?.(); },
    seekbackward:  (d) => { const m = media(); if (m) m.currentTime = Math.max(0, m.currentTime - (d?.seekOffset || SKIP_SECONDS)); },
    seekforward:   (d) => { const m = media(); if (m) m.currentTime = Math.min(m.duration || Infinity, m.currentTime + (d?.seekOffset || SKIP_SECONDS)); },
    seekto:        (d) => { const m = media(); if (m && typeof d?.seekTime === "number") m.currentTime = d.seekTime; },
    // Mapped to skips rather than left unset: on a single long recording
    // there is no previous track, and an unhandled action greys the
    // button out, which looks like the app half-works.
    previoustrack: () => { const m = media(); if (m) m.currentTime = Math.max(0, m.currentTime - SKIP_SECONDS); },
    nexttrack:     () => { const m = media(); if (m) m.currentTime = Math.min(m.duration || Infinity, m.currentTime + SKIP_SECONDS); },
  };

  const wired = [];
  Object.entries(handlers).forEach(([action, fn]) => {
    try { ms.setActionHandler(action, fn); wired.push(action); }
    catch { /* action unsupported on this platform; the rest still work */ }
  });

  return function teardown() {
    try { ms.metadata = null; } catch {}
    try { ms.playbackState = "none"; } catch {}
    wired.forEach((action) => { try { ms.setActionHandler(action, null); } catch {} });
  };
}

/** Keep the lock screen's play/pause in step with the real element. */
export function setPlaybackState(state) {
  if (!hasMediaSession()) return;
  try { navigator.mediaSession.playbackState = state; } catch {}
}

/**
 * The scrubber on the lock screen, and the elapsed time.
 *
 * Guarded hard: setPositionState throws if duration is NaN or position
 * runs past it, which happens routinely while a stream is still loading.
 */
export function setPositionState(media) {
  if (!hasMediaSession() || !media) return;
  try {
    const duration = Number(media.duration);
    const position = Number(media.currentTime);
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (!Number.isFinite(position) || position < 0 || position > duration) return;
    navigator.mediaSession.setPositionState({
      duration,
      position,
      playbackRate: Number(media.playbackRate) || 1,
    });
  } catch { /* browsers disagree about when this is legal */ }
}

export { SKIP_SECONDS };
